import React, { FC, useEffect, useMemo, useRef, useState } from 'react';
import { styled } from 'linaria/react';

import CirclesWebm from 'assets/backgrounds/circles.webm';
import Digital1Webm from 'assets/backgrounds/digital_1.webm';
import Digital2Webm from 'assets/backgrounds/digital_2.webm';
import EclipseWebm from 'assets/backgrounds/eclipse.webm';
import RippleWebm from 'assets/backgrounds/ripple.webm';
import GlassWebm from 'assets/backgrounds/glass.webm';

type Positioning = React.CSSProperties & { className?: string };

interface IAnimationItem {
  webm: string;
  // Optional per-animation inline styles (position, transform, size)
  style?: Positioning;
}

const animations: IAnimationItem[] = [
  // Centered circle pulses
  { webm: CirclesWebm },
  // Digital patterns
  { webm: Digital1Webm },
  { webm: Digital2Webm },
  // Eclipse
  { webm: EclipseWebm },
  // Ripple
  { webm: RippleWebm },
  // Glass shards
  { webm: GlassWebm },
];

const VideoWrap = styled.div<{ blendMode?: string }>`
  position: absolute;
  top: 0;
  left: calc(50% - 50vw);
  width: 100vw;
  height: 420px; /* matches default overlay height usage */
  pointer-events: none;
  z-index: 0;
  overflow: hidden;
  mix-blend-mode: ${({ blendMode }) => blendMode || 'normal'};
`;

const StyledVideo = styled.video`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: 100%;
  height: auto;
  max-width: 387px;
  aspect-ratio: 780 / 900;
`;

const isMobileSafari = (): boolean => {
  if (typeof navigator === 'undefined') return false;
  const ua = navigator.userAgent;
  const isIOS = /iP(ad|hone|od)/.test(ua);
  const isSafari = /Safari\//.test(ua) && !/Chrome\//.test(ua) && !/CriOS\//.test(ua) && !/FxiOS\//.test(ua);
  return isIOS && isSafari;
};

const getRandomIndex = (maxExclusive: number): number => Math.floor(Math.random() * maxExclusive);

export interface IProfileVideoBackgroundProps {
  // Allow overriding the height if the overlay height changes
  heightPx?: number;
  blendMode?: string;
}

const ProfileVideoBackground: FC<IProfileVideoBackgroundProps> = ({ heightPx = 420, blendMode = 'screen' }) => {
  const safariMobile = useMemo(() => isMobileSafari(), []);
  const [index] = useState(() => getRandomIndex(animations.length));
  const current = animations[index];
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const timeoutRef = useRef<number | null>(null);
  const [canPlay, setCanPlay] = useState(false);

  useEffect(() => {
    // Try to start playback silently after a 5-second delay; browsers allow muted autoplay
    if (videoRef.current && canPlay) {
      const v = videoRef.current;
      v.muted = true;
      const delayTimeout = window.setTimeout(() => {
        v.play().catch(() => {
          /* ignore */
        });
      }, 5000);
      return () => clearTimeout(delayTimeout);
    }
    return undefined;
  }, [canPlay]);

  useEffect(() => {
    const video = videoRef.current;
    if (!video) {
      return () => {
        // No cleanup needed if video doesn't exist
      };
    }

    const handleEnded = () => {
      // Clear any existing timeout
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
      // Wait 5 seconds before restarting
      timeoutRef.current = window.setTimeout(() => {
        if (video) {
          video.currentTime = 0;
          video.play().catch(() => {
            /* ignore */
          });
        }
      }, 5000);
    };

    video.addEventListener('ended', handleEnded);

    return () => {
      video.removeEventListener('ended', handleEnded);
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  if (safariMobile) {
    return null;
  }

  return (
    <VideoWrap blendMode={blendMode} style={{ height: `${heightPx}px` }}>
      <StyledVideo ref={videoRef} playsInline muted onCanPlay={() => setCanPlay(true)}>
        <source src={current.webm} type="video/webm" />
      </StyledVideo>
    </VideoWrap>
  );
};

export default ProfileVideoBackground;
